<script setup lang="ts">
/**
 * テーブルアラートモーダル
 */
import { computed, defineProps, nextTick, onBeforeUnmount, onUpdated, ref, StyleValue, watch, withDefaults } from 'vue';
import { i18n } from '@/i18n';
import { onMounted } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { AlertBasicDataObject } from '@/coreDomainsModule/alert';

/**
 * propsの定義
 * @param alertData - アラート情報のオブジェクトの配列
 */
type AlertProps = {
  alertData: AlertBasicDataObject[];
  propIsOpening: boolean;
  iconWarning?: string;
  iconError?: string;
  isDescriptionAlignCenter?: boolean;
};

const props = withDefaults(defineProps<AlertProps>(), {
  iconWarning: 'alertCellWarning',
  iconError: 'alertCellError',
});

const emits = defineEmits<{
  // NOTE: alertKindの定義は各Moduleに実装されています
  (e: 'onNext', alertKind: string): void;
  (e: 'click', isOpening: boolean): void;
}>();

const t = i18n.t;

// ランダムID生成
const imgId = ref<string>('');
onMounted(() => {
  imgId.value = Math.random().toString(32).substring(2);
  window.addEventListener('click', onClickBackground);
});

onUpdated(() => {
  if (isOpening.value) {
    const element = document.getElementById(imgId.value + '-modal');
    if (!element) return;
    const elementRect = element.getBoundingClientRect();
    if (elementRect.top < 0) {
      yLength.value = yLength.value + Math.abs(elementRect.top) + 8;
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('click', onClickBackground);
});

const xLength = ref(0);
const yLength = ref(0);
const bubbleHeight = ref(0);
const isOpening = ref(false);
const isHorizontalReverse = ref(true);

/**
 * アラートアイコンを表示するかどうか
 * alertTypesに何か入っているかどうかで判断
 * @param alertData - アラートの種類の配列
 */
const isDisplayAlertIcon = (alertData: AlertBasicDataObject[]) => {
  return alertData.length > 0;
};

const alertIconName = computed((): string => {
  let res = props.iconWarning;
  props.alertData.map((alertDatum) => {
    if (alertDatum.type === 'error') {
      res = props.iconError;
    }
  });
  return res;
});

const alertIconStyle = computed((): StyleValue => {
  if (props.iconWarning !== 'alertCellWarning' || props.iconError !== 'alertCellError') {
    return { padding: '2px' };
  }
  return {};
});

watch(
  () => isOpening.value,
  () => {
    if (isOpening.value) {
      const element = document.getElementById(imgId.value);
      if (!element) return;
      bubbleHeight.value = 0;
      isHorizontalReverse.value = false;
      const iconRect = element.getBoundingClientRect();
      xLength.value = iconRect.left + window.scrollX;
      yLength.value = iconRect.top + window.scrollY;
    }
    emits('click', isOpening.value);
    nextTick(() => {
      const iconRect = document.getElementById(imgId.value)?.getBoundingClientRect();
      const modalRect = document.getElementById(imgId.value + '-modal')?.getBoundingClientRect();
      if (!modalRect || !iconRect) return;
      if (modalRect.left < 0) {
        isHorizontalReverse.value = true;
        xLength.value = iconRect.right + modalRect.width + 8;
      }
      if (modalRect.bottom > window.innerHeight) {
        yLength.value = yLength.value - (modalRect.bottom - window.innerHeight) - 8;
        bubbleHeight.value = modalRect.height / 2 + (modalRect.bottom - window.innerHeight); // 21は吹き出しの高さの半分
      }
    });
  }
);

watch(
  () => props.propIsOpening,
  () => {
    if (isOpening.value && !props.propIsOpening) {
      isOpening.value = false;
    }
  }
);

const getModalStyle = computed((): StyleValue => {
  return {
    top: `${yLength.value}px`,
    left: isHorizontalReverse.value ? `${xLength.value}px` : `${xLength.value - 40}px`, // 40は吹き出しの位置調整
    'grid-template-columns': `repeat(${props.alertData.length}, 1fr)`,
  };
});

const getBubbleStyle = computed(() => {
  return {
    top: bubbleHeight.value ? `${bubbleHeight.value}px` : '50%', // 基本的には50%。モーダルが画面端に来て位置がズレる場合は吹き出しの位置が対象の要素の中心に来るようにする
    left: isHorizontalReverse.value ? '-10px' : 'auto',
    right: !isHorizontalReverse.value ? '-10px' : 'auto',
  };
});

const descriptionStyle = computed((): StyleValue => {
  return { textAlign: props.isDescriptionAlignCenter ? 'center' : 'start' };
});

/**
 * モーダル展開時にバックグラウンドをクリックしたかの判定と処理
 * @param e - イベント
 */
const onClickBackground = (e: MouseEvent) => {
  if (!isOpening.value || !e.target || !(e.target instanceof HTMLElement)) return;
  if (!e.target.closest('.alert-cell__modal')) {
    isOpening.value = false;
  }
};
</script>

<template>
  <div class="alert-cell">
    <SvgLoader
      v-show="isDisplayAlertIcon(alertData)"
      :id="imgId"
      class="alert-cell__status-tag"
      :style="alertIconStyle"
      :name="alertIconName"
      @click="isOpening = !isOpening"
    />

    <Teleport v-if="isOpening" to="body">
      <div :id="imgId + '-modal'" class="alert-cell__modal modal-container" :style="getModalStyle">
        <div class="alert-cell__bubble" :style="getBubbleStyle" />
        <template v-for="(alertDatum, index) in alertData" :key="'alert' + index">
          <div class="alert-body">
            <div class="alert-body__title">
              <template v-if="alertDatum.type === 'error'">
                <div class="alert-body__title__main">
                  <SvgLoader class="alert-body__title__icon alert-body__title__icon--error" name="calendarAlert" />
                  <span class="alert-body__title__main__text alert-body__title__text--alert">{{ alertDatum.title }}</span>
                </div>
                <div class="alert-body__title__sub">
                  <span class="alert-body__title__sub__text alert-body__title__text--alert">{{ alertDatum.subtitle }}</span>
                </div>
              </template>
              <template v-else-if="alertDatum.type === 'warning'">
                <div class="alert-body__title__main">
                  <SvgLoader class="alert-body__title__icon alert-body__title__icon--warning" name="warning" />
                  <span class="alert-body__title__main__text alert-body__title__text--warning">{{ alertDatum.title }}</span>
                </div>
                <div class="alert-body__title__sub">
                  <span class="alert-body__title__sub__text alert-body__title__text--warning">{{ alertDatum.subtitle }}</span>
                </div>
              </template>
            </div>
            <div class="alert-body__label" :style="descriptionStyle">
              {{ alertDatum.description }}
            </div>
            <div v-if="$slots[`alertBody-${alertDatum.kind}`]" class="alert-body__content">
              <slot :name="`alertBody-${alertDatum.kind}`" />
            </div>
            <div v-if="alertDatum.showConfirmButton" class="alert-body__confirm">
              <TextButton size="btn-sm" @click="emits('onNext', alertDatum.kind)">
                {{ t('確認する') }}
              </TextButton>
            </div>
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.alert-cell {
  position: relative;
  display: grid;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  &__status-tag {
    width: 20px;
    height: 20px;
    margin: auto;
    cursor: pointer;
  }

  &__modal {
    display: grid;
    padding: 24px 24px;
    background-color: $semanticWhite;
    box-shadow: 0px 0px 22px 0px #80808066;
    position: absolute;
    z-index: 22; // 2025/4/29: src/components/modal/Modal.vueに負けていたため更新
    transform: translateX(-100%) translateY(-45%);
    right: 50px;

    margin-left: 12px;
    border-radius: 8px;

    width: min-content; // 必要に応じて幅を調整
    min-width: 250px; // 最小幅を設定
  }

  &__bubble {
    display: block;
    position: absolute;
    width: 30px; // 三角形の幅
    height: 30px; // 三角形の高さ
    background-color: $semanticWhite; // 三角形の背景色を吹き出しと同じに
    transform: translateY(-50%) rotate(-135deg); // 三角形を-135度回転
    z-index: 23; // 2025/4/29: src/components/modal/Modal.vueに負けていたため更新
  }
}

.modal-container {
  display: grid;
  column-gap: 24px;
}

.alert-body {
  display: grid;
  height: 100;
  min-width: 352px;
  row-gap: 24px;
  justify-content: center;
  align-content: space-between;

  &:not(:last-child) {
    border-right: 2px solid $borderDefault;
    padding-right: 24px; // 境界線の仕切りを調整
    min-width: 378px; // alert-bodyのmargin + padding + borderを考慮
  }

  &__title {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));
    align-items: center;
    justify-items: center;
    margin: auto;
    column-gap: 4px;

    &__main {
      display: grid;
      grid-template-columns: repeat(2, fit-content(100%));
      align-items: center;
      justify-items: center;
      margin: auto;
      column-gap: 4px;

      &__text {
        font-size: 22px;
        font-weight: 700;
        line-height: 28px;
      }
    }

    &__sub {
      &__text {
        font-size: 16px;
        font-weight: 700;
        line-height: 16px;
      }
    }

    &__icon {
      width: 20px;
      height: 20px;
      &--error {
        margin-bottom: 4px;
      }
      &--warning {
        margin-bottom: 8px;
      }
    }

    &__text {
      &--alert {
        color: $semanticError;
      }

      &--warning {
        color: $semanticWarning;
      }
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
    white-space: pre-line;
  }

  &__content {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));
    row-gap: 8px;
    padding: 16px 8px;
    align-items: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
    background-color: $backgroundDefault;
    border-radius: 8px;
  }

  &__confirm {
    display: grid;
    justify-content: center;
  }
}
</style>
